import { useEffect, useMemo, useRef, useState } from 'react'
import api from '@/api/config'

export type CampaignTaskKind = 'generation' | 'sending'

export interface CampaignTaskSnapshot {
  campaignUuid: string
  taskId: string
  kind: CampaignTaskKind
  state: string
}

function normalizeState(statusData: any): string {
  const raw = (statusData?.state ?? statusData?.status ?? '').toString().trim().toLowerCase()
  return raw || 'running'
}

function looksLikeSendingTask(statusData: any) {
  // Sending task returned by /api/task/:id/status may include these canonical keys.
  // Be permissive: different deployments/versions may use slightly different names.
  const keys = statusData ? Object.keys(statusData) : []

  const hasCanonicalCounts =
    typeof statusData?.total_emails === 'number' || typeof statusData?.sent_emails === 'number' || typeof statusData?.failed_emails === 'number'

  const hasAltCounts =
    typeof statusData?.total === 'number' || typeof statusData?.sent === 'number' || typeof statusData?.failed === 'number'

  const hasCampaignField = typeof statusData?.campaign === 'object' && !!statusData?.campaign

  // Heuristic: sending status endpoints in this codebase tend to expose total/sent/failed and often a campaign object.
  return (hasCanonicalCounts || hasAltCounts) && (hasCampaignField || keys.includes('delay_minutes') || keys.includes('strategy'))
}

const terminalStates = new Set(['completed', 'failed', 'cancelled', 'error'])

const EMPTY_CANDIDATES: any[] = []
const EMPTY_SNAPSHOTS: Record<string, CampaignTaskSnapshot> = {}

/**
 * Best-effort campaign background task detection.
 */
export function useCampaignBackgroundTasks(
  campaigns: Array<{ uuid: string; task_id?: string; status?: string; processing_status?: string; latest_sending_task_id?: string }>,
  opts?: { pollMs?: number },
) {
  const pollMs = opts?.pollMs ?? 7000

  const [snapshots, setSnapshots] = useState<Record<string, CampaignTaskSnapshot>>(EMPTY_SNAPSHOTS)
  const timerRef = useRef<number | null>(null)

  const taskCandidates = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return EMPTY_CANDIDATES
    return campaigns
      .filter(c => !!c.task_id || !!(c as any).latest_sending_task_id)
      .flatMap(c => {
        const campaignUuid = c.uuid
        const campaignStatus = (c.status || '').toString().toLowerCase()
        const processingStatus = (c.processing_status || '').toString().toLowerCase()

        const candidates: Array<{ campaignUuid: string; taskId: string; hintedKind?: CampaignTaskKind; campaignStatus: string; processingStatus: string }> = []

        // Prefer dedicated sending task id when present.
        const sendingTaskId = (c as any).latest_sending_task_id
        if (sendingTaskId) {
          candidates.push({ campaignUuid, taskId: sendingTaskId, hintedKind: 'sending', campaignStatus, processingStatus })
        }

        // Keep legacy generation task id candidate (TaskManager) when present.
        if (c.task_id) {
          candidates.push({ campaignUuid, taskId: c.task_id as string, hintedKind: 'generation', campaignStatus, processingStatus })
        }

        return candidates
      })
  }, [campaigns])

  useEffect(() => {
    let cancelled = false

    const stop = () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current)
        timerRef.current = null
      }
    }

    const run = async () => {
      if (taskCandidates.length === 0) {
        setSnapshots(prev => (Object.keys(prev).length === 0 ? prev : EMPTY_SNAPSHOTS))
        return
      }

      const next: Record<string, CampaignTaskSnapshot> = {}

      await Promise.all(
        taskCandidates.map(async ({ campaignUuid, taskId, hintedKind, campaignStatus, processingStatus }) => {
          try {
            const res = await api.get(`/api/task/${taskId}/status`)
            const data = res.data
            const state = normalizeState(data)

            if (terminalStates.has(state)) return

            let kind: CampaignTaskKind
            if (hintedKind) {
              kind = hintedKind
            } else if (looksLikeSendingTask(data)) {
              kind = 'sending'
            } else {
              const hintedSending =
                campaignStatus.includes('sending') ||
                processingStatus.includes('sending') ||
                processingStatus.includes('send')
              kind = hintedSending ? 'sending' : 'generation'
            }

            // Prefer sending snapshot over generation for same campaign if both are present.
            const existing = next[campaignUuid]
            if (!existing || (existing.kind === 'generation' && kind === 'sending')) {
              next[campaignUuid] = { campaignUuid, taskId, kind, state }
            }
          } catch {
            // Ignore
          }
        }),
      )

      if (cancelled) return
      setSnapshots(prev => {
        const prevKeys = Object.keys(prev)
        const nextKeys = Object.keys(next)
        if (prevKeys.length !== nextKeys.length) return next

        const hasChanged = nextKeys.some(k => {
          const p = prev[k]
          const n = next[k]
          return !p || p.state !== n.state || p.taskId !== n.taskId || p.kind !== n.kind
        })

        return hasChanged ? next : prev
      })
    }

    stop()
    run()
    timerRef.current = window.setInterval(run, pollMs)

    return () => {
      cancelled = true
      stop()
    }
  }, [taskCandidates, pollMs])

  const sendingCampaigns = useMemo(() => {
    return Object.values(snapshots).filter(s => s.kind === 'sending')
  }, [snapshots])

  const generationCampaigns = useMemo(() => {
    return Object.values(snapshots).filter(s => s.kind === 'generation')
  }, [snapshots])

  return {
    snapshots,
    sendingCampaigns,
    generationCampaigns,
  }
}
